import React, { useState, useCallback } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Field,
  FormErrorMessage,
  HeaderSection,
  Link,
  Text,
  Container,
} from '@groupelacentrale/contact-storybook';
import { IconHelp } from '@groupelacentrale/contact-storybook/lib/icons';
import { FormikTouched } from 'formik';
import './RegistrationForm.scss';
import { RegistrationHelperModal } from './RegistrationHelperModal';
import { LegalNotice } from 'components';
import { keypressCallback } from 'utils';

interface Props {
  immatriculation: string;
  vin: string;
  vinError: boolean;
  onVinErrorClick: (e: React.MouseEvent) => void | undefined;
  isPro: boolean;
  autorisation: boolean;
  handleSubmit: () => undefined | void;
  handleChange: (e: React.FormEvent<HTMLInputElement>) => undefined | void;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  isSubmitting: boolean;
  errors: { immatriculation?: any; vin?: any; autorisation?: any };
  errorMessage?: string;
  touched: FormikTouched<{ immatriculation: string; vin: string; autorisation: boolean }>;
  parkingFull?: boolean;
}

export const RegistrationFormView: React.FC<Props> = ({
  immatriculation,
  vin,
  vinError,
  onVinErrorClick,
  autorisation,
  isPro,
  handleSubmit,
  handleChange,
  setFieldValue,
  isSubmitting,
  errors,
  errorMessage,
  touched,
  parkingFull,
}) => {
  const [isLegalNoticeModalOpen, setIsLegalNoticeModalOpen] = useState(false);
  const [isHelperModalOpen, setIsHelperModalOpen] = useState(false);

  const capitalizeOnChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      e.currentTarget.value = e.currentTarget.value.toUpperCase();
      handleChange(e);
    },
    [handleChange],
  );

  const onKeyPress = useCallback((e: React.KeyboardEvent) => {
    const match = e.key.match(/[0-9a-zA-Z]/);
    if (match && match.length) {
      return true;
    }
    e.preventDefault();
    return false;
  }, []);

  const handleKeyPressCheck = keypressCallback(
    (option: 'string') => setFieldValue(option, !autorisation),
    'data-option',
  );

  const onPaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const paste = e.clipboardData.getData('text');
      const name = (e.target as HTMLInputElement).name;
      const value =
        paste
          .match(/[0-9a-zA-Z]/g)
          ?.join('')
          .toUpperCase() || '';
      setFieldValue(name, value);
    },
    [setFieldValue],
  );

  return (
    <Container shadowed paddings="mediumP" backgroundColor="bgWhite">
      <form className="registration-form" onSubmit={handleSubmit}>
        <HeaderSection className="titleSection" subTitle="Munissez vous de votre carte grise" noUnderline>
          Démarrez votre dépôt d&apos;annonce
        </HeaderSection>
        {parkingFull ? (
          <Alert type="error">{errorMessage}</Alert>
        ) : (
          <>
            <Field
              name="immatriculation"
              label="Numéro d'immatriculation*"
              placeholder="AA123BB"
              value={immatriculation}
              onChange={capitalizeOnChange}
              onKeyPress={onKeyPress}
              onPaste={onPaste}
              errorMessage={touched.immatriculation ? errors.immatriculation : undefined}
            />
            {isPro && (
              <Text align="center" weight="bold">
                ou
              </Text>
            )}

            <Field
              name="vin"
              label={
                isPro ? (
                  'Numéro d’identification (VIN)*'
                ) : (
                  <div className="help-wrapper">
                    3 derniers caractères du numéro d&apos;identification*&nbsp;{' '}
                    <IconHelp style={{ cursor: 'pointer' }} width={20} onClick={() => setIsHelperModalOpen(true)} />
                  </div>
                )
              }
              placeholder={isPro ? undefined : '123 (voir champ E. sur votre carte grise)'}
              value={vin}
              onChange={capitalizeOnChange}
              onKeyPress={onKeyPress}
              onPaste={onPaste}
              errorMessage={touched.vin ? errors.vin : undefined}
            />
            <div tabIndex={0} data-option="autorisation" onKeyPress={handleKeyPressCheck}>
              <Checkbox
                name="autorisation"
                theme="primary"
                value="autorisation"
                label={
                  <>
                    J&apos;autorise La Centrale à accéder aux données techniques de ma carte grise et pré-remplir mon
                    annonce.*
                    <br />
                    <span
                      className="legal-notice-link"
                      onClick={e => {
                        e.preventDefault();
                        setIsLegalNoticeModalOpen(true);
                      }}
                    >
                      Informations légales
                    </span>
                  </>
                }
                onClick={handleChange}
                checked={autorisation}
                hasError={touched.autorisation && !!errors.autorisation}
              />
            </div>
            {touched.autorisation && errors.autorisation && (
              <FormErrorMessage name="autorisation" message={errors.autorisation} />
            )}
            {vinError && (
              <Alert type="error">
                <p>Dépôt impossible avec cette immatriculation</p>
                <p>
                  Afin de déposer votre annonce sur notre site, vous devez saisir le numéro d&apos;immatriculation de
                  votre véhicule ainsi que les 3 derniers caractères d&apos;identification (VIN) indiqué sur le champs
                  E. de votre carte grise. Si cela ne fonctionne pas, et afin d&apos;effectuer des vérifications,
                  pourriez-vous nous transmettre&nbsp;
                  <a href="mailto:info-centrale@lacentrale.fr" onClick={onVinErrorClick} style={{ color: 'inherit' }}>
                    <b>par email</b>
                  </a>
                  &nbsp;la copie de votre carte grise, ou à défaut :
                </p>
                <ul>
                  <li>le numéro d&apos;immatriculation de votre véhicule </li>
                  <li>
                    le numéro d&apos;identification (= VIN) dans son intégralité, que vous trouverez sur votre carte
                    grise, à la ligne E.
                  </li>
                </ul>
              </Alert>
            )}
            {errorMessage && <Alert type="error">{errorMessage}</Alert>}
            <Text size="small">* Champs obligatoires</Text>
            <Button label="Continuer" type="submit" theme="primary" disabled={isSubmitting} loading={isSubmitting} />
            <Text size="small" className="data-info">
              Les données que vous renseignez dans ce formulaire sont traitées par le Groupe La Centrale en qualité de
              responsable de traitement.
              <br />
              Les données obligatoires sont celles signalées par un astérisque dans ce formulaire.
              <br />
              Ces données sont utilisées à des fins de :<br />
              - fourniture des services qui vous sont proposés, en tant qu’annonceur, sur La Centrale (et notamment de
              publication d’une annonce, de mise en relation avec des acheteurs, etc.);
              <br />
              - gestion de votre relation commerciale avec le Groupe La Centrale; <br />
              - mesure de l’audience de la plateforme;
              <br />
              - lutte anti-fraude.
              <br />
              Vous disposez d’un droit d’accès, de rectification, d’effacement de ces données, d’un droit de limitation
              du traitement, d’un droit d’opposition, du droit à la portabilité de vos données et du droit d’introduire
              une réclamation auprès d’une autorité de contrôle (en France, la CNIL). <br />
              Pour en savoir plus sur le traitement de vos données :<br />
              <Link theme="primaryLink" underline href="https://www.lacentrale.fr/politiqueconfidentialite.php">
                https://www.lacentrale.fr/politiqueconfidentialite.php
              </Link>
            </Text>
          </>
        )}
      </form>
      <LegalNotice isOpen={isLegalNoticeModalOpen} onClose={() => setIsLegalNoticeModalOpen(false)} />
      <RegistrationHelperModal isOpen={isHelperModalOpen} onClose={() => setIsHelperModalOpen(false)} />
    </Container>
  );
};
